import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import Avatar from '../components/Avatar';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { useConfirm } from '../components/ConfirmModal';

function EditModal({ title, fields, onClose, onSave }) {
  const [values, setValues] = useState(
    Object.fromEntries(fields.map(f => [f.key, f.defaultValue || '']))
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onSave(values);
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-text mb-4">{title}</h3>
        <form onSubmit={handleSubmit}>
          {fields.map((field) => (
            <div key={field.key} className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">{field.label}</label>
              <input
                type={field.type || 'text'}
                value={values[field.key]}
                onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                required
                placeholder={field.placeholder || ''}
                className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
              />
            </div>
          ))}
          {error && (
            <div className="mb-3 p-2 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 disabled:opacity-50">
              {loading ? 'Saving...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InviteModal({ clinicId, userId, onClose, onInvited }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.settings.invite(clinicId, {
        email,
        first_name: firstName,
        last_name: lastName,
        created_by: userId,
      });
      onInvited();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-text mb-4">Invite Member</h3>
        <form onSubmit={handleSubmit}>
          <div className="flex gap-3 mb-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-text mb-1">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-text mb-1">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-text mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
            />
          </div>
          {error && (
            <div className="mb-3 p-2 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 disabled:opacity-50">
              {loading ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ClinicSection({ clinic, userId, subscription, onRefresh }) {
  const navigate = useNavigate();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [clinicNameModalOpen, setClinicNameModalOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const { confirm, confirmModal } = useConfirm();

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const session = await api.billing.createPortalSession(clinic.id);
      if (session.url) {
        window.location.href = session.url;
      }
    } catch (err) {
      console.error('Portal session failed:', err);
    }
    setPortalLoading(false);
  };

  const handleRemoveMember = async (member) => {
    const isSelf = member.id === userId;
    const fullName = `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email;
    const ok = await confirm(isSelf
      ? {
          title: 'Leave this clinic?',
          message: `You'll lose access to ${clinic.name}. An admin will need to re-add you.`,
          confirmLabel: 'Leave clinic',
          destructive: true,
        }
      : {
          title: 'Remove member?',
          message: `${fullName} will lose access to ${clinic.name}.`,
          confirmLabel: 'Remove',
          destructive: true,
        });
    if (!ok) return;
    try {
      await api.settings.removeMember(clinic.id, member.id);
      onRefresh();
    } catch (err) {
      console.error('Remove failed:', err);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await api.settings.cancelInvite(clinic.id, inviteId);
      onRefresh();
    } catch (err) {
      console.error('Cancel invite failed:', err);
    }
  };

  return (
    <>
      <div className="border-t border-gray-100 pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
        {/* Clinic Name */}
        <div className="flex items-center justify-between pb-4">
          <div>
            <p className="text-sm font-semibold text-text">Clinic Name</p>
            <p className="text-sm text-gray-500">{clinic.name}</p>
          </div>
          <button
            onClick={() => setClinicNameModalOpen(true)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Update clinic name
          </button>
        </div>

        {/* Subscription Status */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <p className="text-sm font-semibold text-text">Subscription</p>
            {subscription?.status === 'active' || subscription?.status === 'trialing' ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                No Subscription
              </span>
            )}
          </div>
          {subscription?.status === 'active' || subscription?.status === 'trialing' ? (
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {portalLoading ? 'Loading...' : 'Manage Subscription'}
            </button>
          ) : (
            <button
              onClick={() => navigate(`/choose-plan?clinicId=${clinic.id}`)}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 transition-colors"
            >
              Subscribe
            </button>
          )}
        </div>

        {/* Members */}
        <p className="text-sm font-semibold text-text mb-2">Members</p>
        <div className="space-y-0 mb-4">
          {clinic.members.map((member, idx) => (
            <div key={member.id} className={`flex items-center justify-between py-3 px-3 ${idx % 2 === 0 ? 'bg-gray-50' : ''} rounded-lg`}>
              <div>
                <p className="text-sm font-medium text-text">{member.first_name} {member.last_name}</p>
                <p className="text-sm text-gray-500">{member.email}</p>
              </div>
              {member.id !== userId && (
                <button
                  onClick={() => handleRemoveMember(member)}
                  className="px-4 py-2 text-sm font-medium text-destructive bg-white border border-destructive-30 rounded-lg hover:bg-destructive-10 transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Invites */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-text">Invites</p>
          <button
            onClick={() => setInviteModalOpen(true)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1"
          >
            <span>+</span> Invite member
          </button>
        </div>
        {clinic.invites.length === 0 ? (
          <div className="border border-[#E6E6E6] rounded-lg p-6 text-center">
            <p className="text-sm font-semibold text-text">No invites</p>
          </div>
        ) : (
          <div className="space-y-0">
            {clinic.invites.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between py-3 px-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-text">
                    {invite.first_name} {invite.last_name}
                  </p>
                  <p className="text-sm text-gray-500">{invite.email}</p>
                </div>
                <button
                  onClick={() => handleCancelInvite(invite.id)}
                  className="px-3 py-1.5 text-sm font-medium text-destructive border border-destructive-30 rounded-lg hover:bg-destructive-10 transition-colors"
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {clinicNameModalOpen && (
        <EditModal
          title="Update clinic name"
          fields={[{ key: 'name', label: 'Clinic Name', defaultValue: clinic.name }]}
          onClose={() => setClinicNameModalOpen(false)}
          onSave={async (values) => {
            await api.settings.updateClinicName(clinic.id, values.name);
            onRefresh();
          }}
        />
      )}

      {inviteModalOpen && (
        <InviteModal
          clinicId={clinic.id}
          userId={userId}
          onClose={() => setInviteModalOpen(false)}
          onInvited={onRefresh}
        />
      )}
      {confirmModal}
    </>
  );
}

export default function Settings() {
  const { profile, signOut } = useAuth();
  const [clinics, setClinics] = useState([]);
  const [subscriptions, setSubscriptions] = useState({});
  const [loadingClinics, setLoadingClinics] = useState(true);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [editPasswordOpen, setEditPasswordOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const loadClinics = () => {
    if (!profile?.id) return;
    api.settings.clinics()
      .then((clinicList) => {
        setClinics(clinicList);
        // Load subscription status for each clinic
        clinicList.forEach((clinic) => {
          api.billing.subscription(clinic.id)
            .then((sub) => setSubscriptions((prev) => ({ ...prev, [clinic.id]: sub })))
            .catch(() => setSubscriptions((prev) => ({ ...prev, [clinic.id]: null })));
        });
      })
      .catch(console.error)
      .finally(() => setLoadingClinics(false));
  };

  useEffect(() => {
    loadClinics();
  }, [profile?.id]);

  if (!profile) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="max-w-4xl">
      <Breadcrumb items={[
        { label: 'Home', to: '/' },
        { label: 'Account' },
      ]} />

      <h1 className="text-[32px] font-semibold text-text mb-1">Settings</h1>
      <p className="text-sm text-gray-500 mb-8">Manage your preferences and settings below</p>

      {/* Profile Section */}
      <div className="bg-white rounded-xl border border-[#E6E6E6] p-6 mb-6">
        <h2 className="text-lg font-bold text-text mb-4">Profile</h2>

        <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
          <Avatar src={profile.profile_picture_url} name={`${profile.first_name} ${profile.last_name}`} size="lg" />
          <div>
            <p className="text-sm font-medium text-text">Profile picture</p>
            <p className="text-sm text-gray-500">Use a photo that's 512px square or larger</p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4">
          <div>
            <p className="text-sm font-medium text-text">Name</p>
            <p className="text-sm text-gray-500">{profile.first_name} {profile.last_name}</p>
          </div>
          <button
            onClick={() => setEditNameOpen(true)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Edit
          </button>
        </div>
      </div>

      {editNameOpen && (
        <EditModal
          title="Update name"
          fields={[
            { key: 'firstName', label: 'First Name', defaultValue: profile.first_name },
            { key: 'lastName', label: 'Last Name', defaultValue: profile.last_name },
          ]}
          onClose={() => setEditNameOpen(false)}
          onSave={async (values) => {
            await api.settings.updateProfile(values.firstName, values.lastName);
            window.location.reload();
          }}
        />
      )}

      {/* Log in details Section */}
      <div className="bg-white rounded-xl border border-[#E6E6E6] p-6 mb-6">
        <h2 className="text-lg font-bold text-text mb-4">Log in details</h2>

        <div className="flex items-center justify-between pb-4 border-b border-gray-100">
          <div>
            <p className="text-sm font-medium text-text">Email</p>
            <p className="text-sm text-gray-500">{profile.email}</p>
          </div>
          <button
            onClick={() => setEditEmailOpen(true)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Update email
          </button>
        </div>

        <div className="flex items-center justify-between pt-4">
          <div>
            <p className="text-sm font-medium text-text">Password</p>
            <p className="text-sm text-gray-500">Update the password you log in with.</p>
          </div>
          <button
            onClick={() => setEditPasswordOpen(true)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Update password
          </button>
        </div>
      </div>

      {editEmailOpen && (
        <EditModal
          title="Update email"
          fields={[{ key: 'email', label: 'Email', type: 'email', defaultValue: profile.email }]}
          onClose={() => setEditEmailOpen(false)}
          onSave={async (values) => {
            await api.settings.updateEmail(values.email);
            window.location.reload();
          }}
        />
      )}

      {editPasswordOpen && (
        <EditModal
          title="Update password"
          fields={[
            { key: 'password', label: 'New Password', type: 'password' },
            { key: 'confirmPassword', label: 'Confirm Password', type: 'password' },
          ]}
          onClose={() => setEditPasswordOpen(false)}
          onSave={async (values) => {
            if (values.password !== values.confirmPassword) throw new Error('Passwords do not match');
            await api.settings.updatePassword(values.password);
          }}
        />
      )}

      {/* Clinic details Section */}
      <div className="bg-white rounded-xl border border-[#E6E6E6] p-6 mb-6">
        <h2 className="text-lg font-bold text-text mb-4">Clinic details</h2>

        {loadingClinics ? (
          <p className="text-sm text-gray-500">Loading clinics...</p>
        ) : clinics.length === 0 ? (
          <p className="text-sm text-gray-500">You are not a member of any clinic.</p>
        ) : (
          clinics.map((clinic) => (
            <ClinicSection
              key={clinic.id}
              clinic={clinic}
              userId={profile.id}
              subscription={subscriptions[clinic.id]}
              onRefresh={loadClinics}
            />
          ))
        )}
      </div>

      {/* Log out Section */}
      <div className="bg-white rounded-xl border border-[#E6E6E6] p-6 mb-6">
        <h2 className="text-lg font-bold text-text mb-4">Log out</h2>
        <button
          onClick={() => setLogoutModalOpen(true)}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Log out
        </button>
      </div>

      {logoutModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setLogoutModalOpen(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-text mb-2">Sign out</h3>
            <p className="text-sm text-gray-500 mb-6">Are you sure you want to sign out?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setLogoutModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={signOut}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
